import { ChevronDown, X } from '@griply/assets';
import React, { Ref } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from 'shared/components/ui/icon';
import { useTheme } from 'styled-components';

import * as Styled from './form-select-trigger.style';
import { Size } from './types';

export type FormSelectTriggerProps = {
  id?: string;
  isPlaceholder?: boolean;
  noBorders?: boolean;
  size?: Size;
  inheritColor?: boolean;
  noOverflow?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  triggerRef?: Ref<HTMLButtonElement>;
  // End slot precedence: endAdornment, else onClear's button, else the chevron.
  endAdornment?: React.ReactNode;
  onClear?: (event: React.MouseEvent) => void;
  ariaHasPopup?: React.AriaAttributes['aria-haspopup'];
  ariaExpanded?: boolean;
  ariaControls?: string;
  children: React.ReactNode;
};

export const FormSelectTrigger: React.FC<FormSelectTriggerProps> = ({
  id,
  isPlaceholder,
  noBorders,
  size = Size.Medium,
  inheritColor,
  noOverflow,
  disabled,
  onClick,
  triggerRef,
  endAdornment,
  onClear,
  ariaHasPopup,
  ariaExpanded,
  ariaControls,
  children,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const onIconClick = disabled ? undefined : onClick;
  const iconColor = theme.palette.grey[600];

  const clearButton = onClear ? (
    <Styled.ClearButton
      onClick={onClear}
      type="button"
      tabIndex={-1}
      aria-label={t('general.clear')}
    >
      <Icon icon={X} size={16} strokeWidth={2} color={iconColor} />
    </Styled.ClearButton>
  ) : null;

  const chevron = (
    <Styled.IconContainer $disabled={!!disabled} onClick={onIconClick}>
      <Icon icon={ChevronDown} size={16} strokeWidth={2} color={iconColor} />
    </Styled.IconContainer>
  );

  return (
    <Styled.Container $disabled={!!disabled}>
      <Styled.Button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        onClick={onClick}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        $isPlaceholder={!!isPlaceholder}
        $noBorders={!!noBorders}
        $size={size}
        $inheritColor={!!inheritColor}
        $noOverflow={!!noOverflow}
      >
        {children}
      </Styled.Button>
      <Styled.InteractionContainer $inheritColor={!!inheritColor} $size={size}>
        {endAdornment ?? clearButton ?? chevron}
      </Styled.InteractionContainer>
    </Styled.Container>
  );
};
